import React, { useEffect, useState } from "react";
import axios from "axios";
import UserDetailsSidebar from "./userDetailSide";
import { useParams } from "react-router-dom";
import MessagingSection from "./massagesSection";
import { motion } from "framer-motion";
import {
  AlertCircle,
  Clock,
  Flag,
  MessageCircle,
  User,
  Building,
  Mail,
  Phone,
  Package,
  UserCheck,
  Activity,
} from "lucide-react";
import { API_URL } from "../components/config";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

const Ticket = () => {
  const { id } = useParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customerId, setCustomerId] = useState(null);
  const [chatId, setChatId] = useState(null);
  const adminId = localStorage.getItem("adminId");
  const navigate = useNavigate();
  const [ticketState, setTicketState] = useState({ note: "", stage: "Noted" });
  const showSuccessAlert = (title, text) => {
    Swal.fire({
      icon: "success",
      title: title,
      text: text,
      timer: 2000,
      showConfirmButton: false,
      background: "#fff",
      customClass: {
        popup: "rounded-xl",
        title: "text-[#002966]",
        content: "text-gray-600",
      },
    });
  };

  const showErrorAlert = (title, text) => {
    Swal.fire({
      icon: "error",
      title: title,
      text: text,
      background: "#fff",
      customClass: {
        popup: "rounded-xl",
        title: "text-red-600",
        content: "text-gray-600",
      },
    });
  };

  useEffect(() => {
    const fetchTicket = async () => {
      try {
        const response = await axios.get(`${API_URL}/tickets/id/${id}`);
        setTicket(response.data);
        setCustomerId(response.data.customer_id);
        setChatId(response.data.chat_id);
      } catch (err) {
        setError("Error fetching ticket data");
      } finally {
        setLoading(false);
      }
    };

    fetchTicket();
  }, [id]);

  const handleNoteSubmit = async (e) => {
    e.preventDefault();
    if (!ticketState.note.trim()) return;

    const payload = {
      ticket_id: chatId,
      assigned_user_id: adminId,
      action_notes: ticketState.note,
      received_at_date_time: new Date().toISOString(),
      left_at_date_time: new Date().toISOString(),
      status: ticketState.stage,
      pinned_images_url: "http://example.com/image1.jpg",
    };

    try {
      await axios.post(`${API_URL}/tickectprogress`, payload);
      setTicketState((prev) => ({ ...prev, note: "" })); // Reset note
    } catch (err) {
      setError(err.message);
      console.error("Error submitting note:", err);
    }
  };

  const handleStageChange = async (e, newStage) => {
    await handleNoteSubmit(e); // Pass the event to handleNoteSubmit
    setTicketState((prev) => ({ ...prev, stage: newStage })); // Update stage

    try {
      if (newStage == "Escalated") {
        await axios.post(
          `${API_URL}/tickectprogress/escalet_ticket/${adminId}/${chatId}`
        );
      } else if (newStage == "Closed") {
        await axios.put(`${API_URL}/tickets/stage/${id}`, { stage: newStage });
        showSuccessAlert(
          "Ticket Closed",
          "The ticket has been successfully closed"
        );
        navigate("/tickets");
      }
    } catch (err) {
      setError(
        `Failed to ${newStage.toLowerCase()} the ticket. ${err.message}`
      );
    }
  };
  const handleEscalete = async (e, newStage) => {
    await handleNoteSubmit(e); // Pass the event to handleNoteSubmit
    setTicketState((prev) => ({ ...prev, stage: newStage })); // Update stage

    try {
      if (newStage) {
        console.log(
          "Escalating ticket...",
          ticket.conversation_support_id,
          adminId
        );
        await axios.post(
          `${API_URL}/tickectprogress/escalet_ticket/${adminId}/${ticket.conversation_support_id}`
        );
        showSuccessAlert(
          "Ticket Escaleted",
          "The ticket has been successfully Escaleted"
        );
        navigate("/tickets");
      } else if (newStage == "Closed") {
        await axios.put(`${API_URL}/tickets/stage/${id}`, { stage: "Closed" });
      }
    } catch (err) {
      setError(
        `Failed to ${newStage.toLowerCase()} the ticket. ${err.message}`
      );
    }
  };

  if (loading)
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#ffc000] border-t-transparent"></div>
      </div>
    );

  if (error)
    return (
      <div className="text-red-500 p-4 bg-red-50 rounded-lg flex items-center gap-2">
        <AlertCircle className="w-5 h-5" />
        {error}
      </div>
    );

  return (
    <div className="flex bg-[#002966] min-h-screen">
      <MessagingSection id={chatId} adminId={adminId} customerId={customerId} />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex-grow bg-[#002966] max-w-2xl mx-auto p-6"
      >
        <div className="bg-[#002966] p-4 text-white shadow-lg">
          <h2 className="text-xl font-semibold">Ticket Handle</h2>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-bold mb-6 text-[#002966] border-b pb-4">
            Ticket Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-[#002966]" />
                <div>
                  <p className="text-sm text-gray-500">Customer Name</p>
                  <p className="font-medium">
                    {ticket.customer_name} {ticket.surname}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Building className="w-5 h-5 text-[#002966]" />
                <div>
                  <p className="text-sm text-gray-500">Company</p>
                  <p className="font-medium">{ticket.company}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#002966]" />
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium">{ticket.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#002966]" />
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="font-medium">
                    {ticket.phone1 || ticket.phone2}
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Package className="w-5 h-5 text-[#002966]" />
                <div>
                  <p className="text-sm text-gray-500">Product</p>
                  <p className="font-medium">{ticket.product}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <UserCheck className="w-5 h-5 text-[#002966]" />
                <div>
                  <p className="text-sm text-gray-500">Assigned To</p>
                  <p className="font-medium">{ticket.person_assigned}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Flag className="w-5 h-5 text-[#002966]" />
                <div>
                  <p className="text-sm text-gray-500">Priority Level</p>
                  <p className="font-medium">{ticket.priority_level}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <MessageCircle className="w-5 h-5 text-[#002966]" />
                <div>
                  <p className="text-sm text-gray-500">Conversation Stage</p>
                  <p className="font-medium">{ticket.conversation_stage}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-6">
            <div className="flex items-center gap-3 mb-4">
              <Clock className="w-5 h-5 text-[#002966]" />
              <div>
                <p className="text-sm text-gray-500">Expiry Date</p>
                <p className="font-medium">
                  {new Date(ticket.expiry_date_time).toLocaleString()}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Activity className="w-5 h-5 text-[#002966]" />
              <div>
                <p className="text-sm text-gray-500">Description</p>
                <p className="font-medium">{ticket.description}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold mb-4 text-[#002966]">
            Notes and Actions
          </h3>
          <form onSubmit={handleNoteSubmit} className="mb-6">
            <textarea
              rows="3"
              value={ticketState.note}
              onChange={(e) =>
                setTicketState((prev) => ({ ...prev, note: e.target.value }))
              }
              placeholder="Add a note..."
              className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#ffc000] focus:border-transparent outline-none transition-all duration-200"
            />
            <button
              type="submit"
              className="mt-2 bg-[#002966] text-white rounded-lg px-6 py-2 hover:bg-[#001f4d] transition-colors duration-200"
            >
              Add Note
            </button>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={(e) => handleStageChange(e, "Closed")}
                className="flex-1 bg-red-500 text-white rounded-lg px-6 py-3 hover:bg-red-600 transition-colors duration-200"
              >
                Close Ticket
              </button>
              <button
                type="button"
                onClick={(e) => handleEscalete(e, "Escalated")}
                className="flex-1 bg-[#ffc000] text-[#002966] font-semibold rounded-lg px-6 py-3 hover:bg-[#e6ac00] transition-colors duration-200"
              >
                Escalate Ticket
              </button>
            </div>
          </form>
        </div>
      </motion.div>
      <UserDetailsSidebar customerId={ticket.customer_id} />
    </div>
  );
};

export default Ticket;
